export type HelpArticle = {
  id: string;
  section: string;
  title: string;
  summary: string;
  body: string;
  appliesTo: string[];
  steps: string[];
  tips?: string[];
  relatedTabs?: string[];
};

export type HelpSection = {
  id: string;
  title: string;
  description: string;
};

export type HelpAssistantConfidence = 'high' | 'medium' | 'low';

export type HelpAssistantMatch = {
  article: HelpArticle;
  excerpt: string;
  matchedTerms: string[];
  score: number;
};

export type HelpAssistantAnswer = {
  confidence: HelpAssistantConfidence;
  matches: HelpAssistantMatch[];
  query: string;
  steps: string[];
  summary: string;
  tips: string[];
};

export const HELP_SECTIONS: HelpSection[] = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    description: 'The fastest path from a new workspace to a useful monitoring routine.',
  },
  {
    id: 'operations',
    title: 'Operations',
    description: 'Daily monitoring, live data checks, and site-level visibility.',
  },
  {
    id: 'configuration',
    title: 'Configuration',
    description: 'Devices, sites, dashboards, alerts, MQTT bridges, and exports.',
  },
  {
    id: 'governance',
    title: 'Governance',
    description: 'Team access, audit review, plan limits, branding, and security.',
  },
  {
    id: 'support',
    title: 'Support',
    description: 'Troubleshooting routines and what to include when contacting Seedronics support.',
  },
];

export const HELP_ARTICLES: HelpArticle[] = [
  {
    id: 'mqtt-credentials',
    section: 'configuration',
    title: 'MQTT credentials: username vs publish topic',
    summary: 'The device topic is your MQTT username. Gateway devices must publish to the gateway topic instead.',
    body: 'Every device gets an auto-generated device topic (for example seedronics/my-sensor-a1b2c3) and an API key. Together they are the MQTT username and password — they only prove WHO is connecting. Where you are allowed to publish is a separate permission (ACL): direct devices publish to their own device topic, but gateway-backed devices are only allowed to publish to the gateway topic. Publishing to the wrong topic is silently dropped at QoS 0, so the client looks connected while no data arrives.',
    appliesTo: ['mqtt', 'devices', 'onboarding', 'live', 'support'],
    steps: [
      'Connect with username = device topic, password = device API key (from the Devices page or the onboarding credential packet).',
      'Direct device: publish to the device topic itself.',
      'Gateway device: publish to the gateway topic (e.g. factory/gw-001) — NOT the username/device topic.',
      'Include the device identifier field in every gateway payload, e.g. {"devId": "493C250702069207", "temperature": 24.5}.',
      'Use QoS 1 while testing so the broker returns an explicit "not authorized" error instead of silently dropping the message.',
    ],
    tips: [
      'Accepted identifier keys: devId, deviceId, id, device_id, identifier, node.',
      'If the broker accepts the connection but Live Sensors stays empty, you are almost always publishing to the wrong topic or missing the identifier field.',
      'The onboarding wizard labels these explicitly: MQTT Username, API Key (MQTT password), and Gateway Topic (publish target).',
    ],
    relatedTabs: ['Onboarding', 'Devices', 'MQTT Bridges'],
  },
  {
    id: 'setup-sequence',
    section: 'getting-started',
    title: 'Recommended setup sequence',
    summary: 'Set up a tenant in the order that makes later monitoring easier to trust.',
    body: 'Most Seedronics workspaces become useful fastest when one clean data path is proven before the rest of the fleet is added. Start with a real site, one known device, one dashboard, one alert, and one responsible teammate.',
    appliesTo: ['overview', 'onboarding', 'devices', 'sites', 'dashboards', 'alerts', 'team', 'general'],
    steps: [
      'Create the site and area where the first device belongs.',
      'Register one device and confirm its MQTT topic or gateway mapping.',
      'Watch Live Sensors until recent readings appear consistently.',
      'Create a dashboard that answers one repeated operating question.',
      'Add one alert rule and send a test notification.',
      'Invite the people who will monitor or respond.',
    ],
    tips: [
      'Use the Onboarding checklist as the readiness score for the workspace.',
      'Do not bulk-add a large device fleet until one complete data path is verified.',
    ],
    relatedTabs: ['Onboarding', 'Sites', 'Devices', 'Live Sensors'],
  },
  {
    id: 'operating-picture',
    section: 'operations',
    title: 'Read the operating picture',
    summary: 'Use Overview to decide whether the platform, a site, or a device needs attention.',
    body: 'Overview is the first daily stop. It combines telemetry volume, online/offline state, alert pressure, and dashboard snapshots so a manager can see whether the workspace is healthy before drilling into a specific asset.',
    appliesTo: ['overview', 'live', 'alerts', 'devices', 'general'],
    steps: [
      'Check WebSocket state and reading volume first.',
      'Review online and offline device counts before interpreting chart values.',
      'Use the site filter when one facility is noisy.',
      'Open recent alert activity to confirm whether issues are new or repeated.',
    ],
    tips: [
      'A quiet overview can still hide stale data if all devices stopped reporting at the same time.',
      'If a chart looks wrong, confirm the latest raw reading before editing widgets or alerts.',
    ],
    relatedTabs: ['Overview', 'Live Sensors', 'Alerts'],
  },
  {
    id: 'live-sensors',
    section: 'operations',
    title: 'Confirm live sensor freshness',
    summary: 'Use Live Sensors to verify that data is arriving and recent.',
    body: 'Live Sensors is the quickest place to confirm that the backend is receiving telemetry. It is also the best starting point when a dashboard, alert, or report looks stale.',
    appliesTo: ['live', 'overview', 'devices', 'mqtt', 'support'],
    steps: [
      'Check the newest timestamp for the device involved.',
      'Compare the reading age with the tenant heartbeat timeout.',
      'Use a site filter to reduce noise when many devices are active.',
      'If readings are missing, inspect the device topic and MQTT bridge next.',
    ],
    tips: [
      'A device can be registered correctly while still producing no readings if the topic or parser does not match the payload.',
      'Screenshots of Live Sensors are useful when opening a support ticket about stale data.',
    ],
    relatedTabs: ['Live Sensors', 'Devices', 'MQTT Bridges'],
  },
  {
    id: 'device-registration',
    section: 'configuration',
    title: 'Register devices cleanly',
    summary: 'Give every physical or virtual stream a clear name, topic, location, and heartbeat policy.',
    body: 'Device records are the foundation for dashboards, alerts, maps, reports, and exports. Clear names and locations make the system easier for operators to scan during incidents.',
    appliesTo: ['devices', 'onboarding', 'live', 'mqtt'],
    steps: [
      'Use a name that an operator can recognize without opening details.',
      'Place the device inside the correct site and area.',
      'Confirm the device topic (MQTT username) and, for gateways, the gateway publish topic exactly match the field setup.',
      'Set heartbeat timeout based on the expected reporting interval.',
      'Use maintenance mode for planned outages so operators do not chase expected downtime.',
    ],
    tips: [
      'For gateway payloads, each virtual device should map to the field that identifies that stream.',
      'Keep test devices clearly named so they do not appear like production assets.',
    ],
    relatedTabs: ['Devices', 'Onboarding', 'MQTT Bridges'],
  },
  {
    id: 'gateway-parsers',
    section: 'configuration',
    title: 'Map gateway payloads',
    summary: 'Use parser settings when one MQTT gateway carries readings for multiple devices.',
    body: 'Gateway-backed deployments often publish one shared topic with a device identifier inside the JSON body. Parser settings tell Seedronics which field identifies the virtual device and where the values live.',
    appliesTo: ['mqtt', 'devices', 'live', 'support'],
    steps: [
      'Confirm a sample payload in the MQTT bridge or broker tools.',
      'Choose the parser mode that matches the payload structure.',
      'Map the device identifier field before mapping individual metrics.',
      'Verify the gateway publishes to the gateway topic — not the device username — or the broker ACL will drop the messages.',
      'Verify Live Sensors shows the virtual device name and fresh values.',
    ],
    tips: [
      'Dot paths and JSON paths must match the incoming payload exactly.',
      'When a gateway changes firmware, re-check parser fields before editing dashboards.',
      'See "Payload examples per parser type" for copy-paste JSON payloads for each parser mode.',
    ],
    relatedTabs: ['MQTT Bridges', 'Devices', 'Live Sensors'],
  },
  {
    id: 'parser-payload-examples',
    section: 'configuration',
    title: 'Payload examples per parser type',
    summary: 'Copy-paste JSON payloads for direct, devId_field, json_path, and custom parsers.',
    body: 'Each parser type expects a different payload shape. In these examples the device identifier is 123456789 and the metrics are gas (10) and water (20) — replace them with your own identifier and field names. Direct devices publish to their own device topic; gateway parsers publish to the gateway topic.',
    appliesTo: ['mqtt', 'devices', 'onboarding', 'live', 'support'],
    steps: [
      'direct — the payload IS the reading, no identifier needed (the topic already identifies the device): {"gas": 10, "water": 20}',
      'devId_field — flat JSON with an identifier field: {"devId": "123456789", "gas": 10, "water": 20}. A nested data object also works: {"devId": "123456789", "data": {"gas": 10, "water": 20}}.',
      'json_path — the identifier is the top-level key and the metrics are nested under it: {"123456789": {"gas": 10, "water": 20}}.',
      'custom — any structure; you map fields yourself in the parser config (e.g. temperature_field = "readings.temp.value"), and dot notation reaches nested values.',
      'Publish gateway payloads to the gateway topic with QoS 1 while testing, then confirm the values appear on Live Sensors.',
    ],
    tips: [
      'The identifier must match the device identifier exactly — a mismatch is rejected and recorded in the Audit log.',
      'devId_field accepts these identifier keys case-insensitively: devId, deviceId, id, device_id, identifier, node.',
      'Standard keys temperature, humidity, pressure, battery (plus aliases like temp/t, hum/h) map to the dedicated columns; every other key (gas, water, fuel, ...) is kept automatically as a custom metric.',
      'See "MQTT credentials: username vs publish topic" for where to publish and which credentials to use.',
    ],
    relatedTabs: ['Onboarding', 'Devices', 'MQTT Bridges', 'Live Sensors'],
  },
  {
    id: 'bridge-json-paths',
    section: 'configuration',
    title: 'Map bridge virtual devices with JSON paths',
    summary: 'How JSON paths work when creating a virtual device from an MQTT bridge topic.',
    body: 'When you create a virtual device from an MQTT bridge, each field mapping points a JSON path at one value inside the payload. Paths accept an optional $. prefix, dot notation for nesting, and [index] for arrays. Example payload: {"customerId":"Vampfi","VI_9207":{"gen1_status":"OFF","fuel_litres":512.28}}.',
    appliesTo: ['mqtt', 'devices', 'live', 'support'],
    steps: [
      'Identify the device block in the payload — here the device identifier is the top-level key VI_9207.',
      'Map each metric with its full path: fuel_litres -> $.VI_9207.fuel_litres (plain VI_9207.fuel_litres without the prefix works too).',
      'Use type String for state values: gen1_status -> $.VI_9207.gen1_status gives "OFF"/"ON".',
      'Reach into arrays with indices: $.sensors[0].temp reads the first element of a sensors array.',
      'Use Scale and Offset to convert units: stored value = raw x scale + offset (e.g. scale 0.1 when the gateway sends tenths of a unit).',
      'Paste a real payload into the Test sandbox before saving — it shows exactly which fields your paths extract.',
    ],
    tips: [
      'Null values are skipped — a device whose block contains only nulls shows no readings until real values arrive.',
      'Numeric strings (e.g. "512.28") are converted to numbers automatically; non-numeric strings are kept as text.',
      'If one bridge message carries several devices (VI_9207, Lekki_9203, ...), create one virtual device per identifier, each with its own paths.',
      'This is the bridge/external-broker mapping. For devices publishing directly to the platform broker, see "Payload examples per parser type".',
    ],
    relatedTabs: ['MQTT Bridges', 'Devices', 'Live Sensors'],
  },
  {
    id: 'sites-and-areas',
    section: 'configuration',
    title: 'Organize sites and areas',
    summary: 'Model facilities so device lists, dashboards, and maps stay easy to filter.',
    body: 'Sites and areas give industrial teams a shared language for where telemetry is coming from. They also make large tenants easier to operate by narrowing views to the facility or zone that matters.',
    appliesTo: ['sites', 'map', 'devices', 'overview'],
    steps: [
      'Create one site per facility, remote location, or operating unit.',
      'Create areas for meaningful zones such as pump room, cold room, inverter yard, or production line.',
      'Assign devices to areas as they are registered.',
      'Use site filters on Overview and Live Sensors during investigations.',
    ],
    tips: [
      'Avoid vague area names like Zone 1 unless that is how the operations team already speaks.',
      'A clean site model makes reports and exports easier to explain later.',
    ],
    relatedTabs: ['Sites', 'Devices', 'Overview'],
  },
  {
    id: 'floor-plan',
    section: 'operations',
    title: 'Use the floor plan during incidents',
    summary: 'Place devices on the map so operators can see where a problem is happening.',
    body: 'The floor plan connects device state to physical layout. It is most useful when a team needs to dispatch someone quickly or compare nearby assets.',
    appliesTo: ['map', 'sites', 'devices', 'alerts'],
    steps: [
      'Upload or choose the correct site layout.',
      'Drag each device to the right area on the plan.',
      'Check online/offline state before sending a technician.',
      'Use alerts and recent readings to decide whether the issue is isolated or area-wide.',
    ],
    tips: [
      'Floor plans are operational tools, so prioritize accuracy over decorative detail.',
      'When a device is moved physically, update its map position in the same maintenance window.',
    ],
    relatedTabs: ['Floor Plan', 'Sites', 'Alerts'],
  },
  {
    id: 'dashboard-builder',
    section: 'configuration',
    title: 'Build dashboards around decisions',
    summary: 'Create dashboards that answer repeated operating questions instead of collecting charts.',
    body: 'A good dashboard has a job. It should help someone decide whether to act, where to look, or what changed over time. Keep each dashboard scoped to a site, process, or audience.',
    appliesTo: ['dashboards', 'overview', 'reports'],
    steps: [
      'Name the dashboard after the decision or operating rhythm it supports.',
      'Use KPI widgets for current state and trend widgets for change over time.',
      'Add status widgets where online/offline state matters.',
      'Keep related devices together so the page can be scanned quickly.',
      'Save and review on a phone if field teams will use it remotely.',
    ],
    tips: [
      'Avoid mixing unrelated facilities in one dashboard unless it is an executive summary.',
      'If a widget is hard to explain, rename it or split the dashboard.',
    ],
    relatedTabs: ['Dashboard Builder', 'Overview', 'Reports'],
  },
  {
    id: 'consumption-analysis',
    section: 'configuration',
    title: 'Choose the right consumption mode',
    summary: 'Match consumption widgets to tank drawdown, cumulative counters, or rate readings.',
    body: 'Consumption widgets depend on how the device reports data. Choosing the wrong mode can make totals look plausible while still being wrong.',
    appliesTo: ['dashboards', 'reports', 'export'],
    steps: [
      'Use tank drawdown when a level sensor drops as liquid or fuel is consumed.',
      'Use cumulative counter when the device reports a total that only increases until reset.',
      'Use rate total when the device reports flow per second, minute, or hour.',
      'Set reset and refill thresholds based on normal site behavior.',
    ],
    tips: [
      'For tanks, confirm capacity and unit before comparing sites.',
      'For counters, look for reset events before trusting a long reporting period.',
    ],
    relatedTabs: ['Dashboard Builder', 'Reports', 'Data Export'],
  },
  {
    id: 'alert-rules',
    section: 'configuration',
    title: 'Make alerts actionable',
    summary: 'Write alert rules that identify a clear condition and the person expected to respond.',
    body: 'Alerts are most useful when they are specific, tested, and quiet enough to trust. A noisy alert stream trains teams to ignore important messages.',
    appliesTo: ['alerts', 'devices', 'overview', 'support'],
    steps: [
      'Choose a metric already visible in recent readings.',
      'Set a threshold that maps to a real action or inspection.',
      'Use cooldowns to prevent repeated messages during one incident.',
      'Send test email or Telegram notifications before relying on a rule.',
      'Review alert history after the first week and tune noisy thresholds.',
    ],
    tips: [
      'If nobody knows what to do when an alert fires, rewrite the alert before enabling it.',
      'A dashboard can help validate an alert threshold before notifications are turned on.',
    ],
    relatedTabs: ['Alerts', 'Dashboard Builder', 'Reports'],
  },
  {
    id: 'telegram-notifications',
    section: 'configuration',
    title: 'Set up Telegram notifications',
    summary: 'Connect the Seedronics bot and learn the one-time step every new user must take before alerts can arrive.',
    body: 'Alert rules can deliver notifications to Telegram via the Seedronics bot. One Telegram rule catches people out: a bot can never start a conversation — it can only reply to users who have messaged it first. So every person who wants Telegram alerts must open the bot and tap Start once. Until they do, sends to their chat ID fail with "chat not found".',
    appliesTo: ['alerts'],
    steps: [
      'In Telegram, search for @Seedronics_bot and open the conversation.',
      'Tap Start (or send any message). This one-time step authorizes the bot to message you — do it before expecting any alerts.',
      'Find your numeric chat ID (the bot or a bot like @userinfobot can show it) and paste it into the alert rule\'s Telegram chat ID field.',
      'Use the rule\'s test button to confirm a message actually arrives.',
      'Repeat for every teammate who wants Telegram alerts — each person must Start the bot themselves.',
    ],
    tips: [
      'If the test send fails with "chat not found", the Start step was skipped — open @Seedronics_bot and tap Start, then test again.',
      'If it fails with a bot token or configuration message, the workspace bot token is missing — ask an admin to check the server configuration.',
      'A group chat also works: add @Seedronics_bot to the group, send any message in the group, then use the group\'s chat ID in the rule.',
    ],
    relatedTabs: ['Alerts'],
  },
  {
    id: 'reports',
    section: 'configuration',
    title: 'Use reports for operating rhythm',
    summary: 'Generate PDFs for shift handovers, maintenance reviews, and management summaries.',
    body: 'Reports are best when they match a recurring business rhythm. The executive report gives leaders the short version; the operational report gives engineers and operators device-level detail.',
    appliesTo: ['reports', 'dashboards', 'settings', 'export'],
    steps: [
      'Choose Executive for management summaries and Operational for device-level review.',
      'Select a period that matches the meeting or handover cadence.',
      'Check branding before sending customer-facing PDFs.',
      'Schedule recurring reports for repeat audiences.',
    ],
    tips: [
      'Use shorter periods for troubleshooting and longer periods for performance review.',
      'If a report raises an anomaly, inspect the source device before changing thresholds.',
    ],
    relatedTabs: ['Reports', 'Settings', 'Data Export'],
  },
  {
    id: 'data-export',
    section: 'configuration',
    title: 'Export clean time-series data',
    summary: 'Use CSV exports when another tool needs raw readings or a focused period of evidence.',
    body: 'Data Export is for analysis outside the dashboard. Keep exports narrow enough that the receiving person can inspect and trust the file.',
    appliesTo: ['export', 'reports', 'devices', 'support'],
    steps: [
      'Choose the device involved in the question.',
      'Select the smallest date range that covers the event.',
      'Preview the export before downloading.',
      'Attach the CSV or a screenshot when support needs raw evidence.',
    ],
    tips: [
      'Use the tenant timezone when matching exported data to local site events.',
      'For recurring business summaries, scheduled reports are usually better than manual exports.',
    ],
    relatedTabs: ['Data Export', 'Reports', 'Support'],
  },
  {
    id: 'team-access',
    section: 'governance',
    title: 'Invite the right roles',
    summary: 'Give teammates the access level that matches their responsibility.',
    body: 'Team access controls who can configure the workspace and who can monitor it. Keep administrator access limited to people who manage setup, users, and security.',
    appliesTo: ['team', 'settings', 'audit', 'general'],
    steps: [
      'Use admin for people who manage workspace settings and users.',
      'Use operator or engineer for people who monitor and respond.',
      'Use viewer for people who only need visibility.',
      'Remove stale invites and inactive users during regular reviews.',
    ],
    tips: [
      'Pending invites can count toward plan user limits.',
      'Use Audit Log after sensitive team or settings changes.',
    ],
    relatedTabs: ['Team', 'Audit Log', 'Settings'],
  },
  {
    id: 'audit-log',
    section: 'governance',
    title: 'Review audit history',
    summary: 'Use audit logs to understand who changed what and when.',
    body: 'The audit log records important user, tenant, device, alert, MQTT, and system actions. It is useful after incidents, configuration changes, and access reviews.',
    appliesTo: ['audit', 'team', 'settings', 'alerts', 'devices'],
    steps: [
      'Filter by severity when looking for errors or critical events.',
      'Filter by action or user when reviewing a specific change.',
      'Open metadata when the summary does not include enough detail.',
      'Capture the audit entry when sending a support ticket about unexpected behavior.',
    ],
    tips: [
      'Audit logs explain platform actions, but they do not replace raw sensor evidence.',
      'Use date filters to keep investigations focused.',
    ],
    relatedTabs: ['Audit Log', 'Team', 'Settings'],
  },
  {
    id: 'branding',
    section: 'governance',
    title: 'Configure workspace branding',
    summary: 'Use eligible plans to show tenant logos and naming across the dashboard and reports.',
    body: 'Branding settings let a tenant present the platform with its own workspace identity. Turning branding off preserves saved values while returning the live dashboard to Seedronics defaults.',
    appliesTo: ['settings', 'reports', 'dashboards'],
    steps: [
      'Open Settings and choose Branding.',
      'Enable custom workspace branding when the plan allows it.',
      'Upload a PNG, JPG, or SVG logo that reads clearly at sidebar size.',
      'Set the platform name, short label, and description.',
      'Generate a test report before sending branded PDFs externally.',
    ],
    tips: [
      'A trial can temporarily unlock branding through the effective trial plan.',
      'If a saved logo fails to render, replace it or ask support to check upload serving.',
    ],
    relatedTabs: ['Settings', 'Reports'],
  },
  {
    id: 'security',
    section: 'governance',
    title: 'Harden account security',
    summary: 'Use 2FA, backup codes, and session review for administrator accounts.',
    body: 'Security settings help administrators protect tenant access. Two-factor authentication and session review matter most for accounts that manage users, devices, alerts, and branding.',
    appliesTo: ['settings', 'team', 'audit', 'general'],
    steps: [
      'Open Settings and choose Security.',
      'Set up authenticator-app 2FA and store backup codes somewhere safe.',
      'Review active sessions after password changes or suspicious activity.',
      'Revoke sessions that are no longer expected.',
    ],
    tips: [
      'Backup codes should be treated like passwords.',
      'Use Audit Log if a session or account change looks unfamiliar.',
    ],
    relatedTabs: ['Settings', 'Audit Log', 'Team'],
  },
  {
    id: 'plan-limits',
    section: 'governance',
    title: 'Understand plan limits',
    summary: 'Plan features and limits decide which panels appear and how many devices, users, and dashboards are available.',
    body: 'Seedronics uses the tenant effective plan to decide which modules and limits are active. During an active trial, the effective plan can be higher than the stored base plan.',
    appliesTo: ['settings', 'devices', 'team', 'dashboards', 'reports', 'general'],
    steps: [
      'Check Settings > General for the current plan and trial state.',
      'Review device, user, and dashboard limits before onboarding a large rollout.',
      'Request an upgrade when a plan blocks the next operating step.',
      'Ask a super admin to adjust trial dates or plan assignment when needed.',
    ],
    tips: [
      'Active trials use the configured trial plan until expiry.',
      'When a trial ends, the tenant falls back to its base plan.',
    ],
    relatedTabs: ['Settings', 'Devices', 'Team'],
  },
  {
    id: 'support-tickets',
    section: 'support',
    title: 'Open a useful support ticket',
    summary: 'Give Seedronics support enough context to reproduce or diagnose the issue quickly.',
    body: 'A strong support ticket names the affected site, device, dashboard, alert, report, or user flow. Screenshots and recent timestamps help support separate UI issues from telemetry or configuration issues.',
    appliesTo: ['support', 'devices', 'alerts', 'reports', 'export', 'live', 'general'],
    steps: [
      'Choose the category closest to the issue.',
      'Set priority based on operating impact, not frustration level.',
      'Name the site, device, dashboard, alert, or report involved.',
      'Attach a screenshot when the UI or data display looks wrong.',
      'Reply in the same thread when support asks for follow-up details.',
    ],
    tips: [
      'Resolved tickets can include a customer-facing explanation of what was fixed.',
      'Closed tickets stay in the archive so the history is still available.',
    ],
    relatedTabs: ['Support', 'Live Sensors', 'Audit Log'],
  },
  {
    id: 'troubleshooting-stale-data',
    section: 'support',
    title: 'Troubleshoot stale or missing data',
    summary: 'Work from raw telemetry to configuration before changing dashboards or alerts.',
    body: 'Stale data can come from a device outage, broker issue, topic mismatch, parser mismatch, or dashboard configuration. Start closest to the incoming data and move outward.',
    appliesTo: ['support', 'live', 'devices', 'mqtt', 'dashboards', 'alerts'],
    steps: [
      'Check Live Sensors for the latest timestamp.',
      'Open the device and confirm topic, parser, site, and heartbeat settings.',
      'Check MQTT Bridges if the device is gateway-backed.',
      'Compare the affected dashboard widget or alert rule with raw readings.',
      'Open a support ticket with screenshots and the expected device topic if the issue remains.',
    ],
    tips: [
      'Avoid editing a dashboard until you know whether the raw data is current.',
      'When multiple devices go stale together, check broker or network connectivity first.',
    ],
    relatedTabs: ['Live Sensors', 'Devices', 'MQTT Bridges', 'Support'],
  },
];

export function getHelpArticlesForTab(tab: string): HelpArticle[] {
  const contextArticles = HELP_ARTICLES.filter((article) => article.appliesTo.includes(tab));
  const generalArticles = HELP_ARTICLES.filter((article) => article.appliesTo.includes('general') && !contextArticles.includes(article));
  return [...contextArticles, ...generalArticles];
}

export function searchHelpArticles(query: string, articles: HelpArticle[] = HELP_ARTICLES): HelpArticle[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return articles;

  return articles.filter((article) => {
    const haystack = [
      article.title,
      article.summary,
      article.body,
      article.section,
      ...article.appliesTo,
      ...article.steps,
      ...(article.tips || []),
      ...(article.relatedTabs || []),
    ].join(' ').toLowerCase();
    return haystack.includes(needle);
  });
}

export function rankHelpArticles(
  query: string,
  activeTab = 'general',
  articles: HelpArticle[] = HELP_ARTICLES
): HelpAssistantMatch[] {
  const tokens = tokenizeHelpQuery(query);
  const phrase = query.trim().toLowerCase();

  return articles.map((article) => {
    const contextual = article.appliesTo.includes(activeTab);
    const titleScore = scoreField(article.title, tokens, 7);
    const summaryScore = scoreField(article.summary, tokens, 5);
    const bodyScore = scoreField(article.body, tokens, 3);
    const sectionScore = scoreField(`${article.section} ${getSectionTitle(article.section)}`, tokens, 3);
    const workflowScore = scoreField(article.steps.join(' '), tokens, 2);
    const tipScore = scoreField((article.tips || []).join(' '), tokens, 2);
    const relatedScore = scoreField([
      ...article.appliesTo,
      ...(article.relatedTabs || []),
    ].join(' '), tokens, 4);
    const phraseScore = phrase.length > 3 && [
      article.title,
      article.summary,
      article.body,
      ...article.steps,
      ...(article.tips || []),
    ].join(' ').toLowerCase().includes(phrase)
      ? 8
      : 0;
    const termScore = titleScore + summaryScore + bodyScore + sectionScore + workflowScore + tipScore + relatedScore + phraseScore;
    const score = termScore + (contextual ? 4 : 0);

    return {
      article,
      excerpt: getArticleExcerpt(article, tokens),
      matchedTerms: getMatchedTerms(article, tokens),
      score,
      termScore,
    };
  })
    .filter((match) => match.termScore > 0 || match.article.appliesTo.includes(activeTab))
    .sort((a, b) => b.score - a.score || a.article.title.localeCompare(b.article.title))
    .map(({ termScore, ...match }) => match);
}

export function answerHelpQuestion(query: string, activeTab = 'general'): HelpAssistantAnswer {
  const matches = rankHelpArticles(query, activeTab).slice(0, 3);
  const confidence = getHelpAnswerConfidence(matches);
  const primary = matches[0]?.article;
  const contextLabel = getHelpContextLabel(activeTab);

  if (!primary) {
    return {
      confidence: 'low',
      matches: [],
      query,
      steps: [
        'Open the full manual and search for the affected page, device, alert, report, or setting.',
        'Open Support if the question depends on tenant data or a recent incident.',
      ],
      summary: `No close manual match was found for "${query}". Try a narrower term, or open Support if this is about a live workspace issue.`,
      tips: [
        'Include the tenant page, device name, timestamp, and a screenshot when opening Support.',
      ],
    };
  }

  const steps = uniqueHelpStrings(matches.flatMap((match) => match.article.steps)).slice(0, 5);
  const tips = uniqueHelpStrings(matches.flatMap((match) => match.article.tips || [])).slice(0, 4);
  const summary = confidence === 'low'
    ? `Closest ${contextLabel} match: ${primary.title}. ${primary.summary} If this does not fit the issue, open Support with the affected workflow and recent evidence.`
    : `Best ${contextLabel} match: ${primary.title}. ${primary.summary}`;

  return {
    confidence,
    matches,
    query,
    steps,
    summary,
    tips,
  };
}

export function getSuggestedHelpQuestions(activeTab: string): string[] {
  const suggestionsByTab: Record<string, string[]> = {
    alerts: ['How do I make alerts actionable?', 'Why are alerts noisy?', 'How do I test notifications?'],
    audit: ['How do I review who changed a setting?', 'What should I capture from audit logs?'],
    dashboards: ['How should I build a dashboard?', 'Which consumption widget should I use?'],
    devices: ['How do I register devices cleanly?', 'Why is a device stale?', 'How should I set heartbeat timeout?'],
    export: ['How do I export clean sensor data?', 'What should I attach to support?'],
    live: ['Why is live data stale?', 'How do I confirm fresh telemetry?', 'What should I check before changing dashboards?'],
    map: ['How should I use the floor plan?', 'How do I locate a device during an incident?'],
    mqtt: ['How do I map gateway payloads?', 'Why is my MQTT device not showing readings?'],
    onboarding: ['What setup order should I follow?', 'When is the workspace ready?'],
    overview: ['How do I read the operating picture?', 'Why can overview look quiet with stale data?'],
    reports: ['Which report type should I use?', 'How do I prepare a customer-facing report?'],
    settings: ['How do I configure branding?', 'How do I harden account security?', 'How do plan limits work?'],
    sites: ['How should I organize sites and areas?', 'How do site filters help investigations?'],
    support: ['How do I open a useful ticket?', 'What evidence should I include for stale data?'],
    team: ['Which role should I invite?', 'How should I review user access?'],
  };

  return uniqueHelpStrings([
    ...(suggestionsByTab[activeTab] || []),
    'How do I troubleshoot stale data?',
    'What should I check before opening support?',
    'Where do I find the full manual?',
  ]).slice(0, 4);
}

export function getHelpArticlesBySection(articles: HelpArticle[] = HELP_ARTICLES) {
  return HELP_SECTIONS.map((section) => ({
    ...section,
    articles: articles.filter((article) => article.section === section.id),
  })).filter((section) => section.articles.length > 0);
}

export function getSectionTitle(sectionId: string) {
  return HELP_SECTIONS.find((section) => section.id === sectionId)?.title || sectionId;
}

const HELP_STOP_WORDS = new Set([
  'about',
  'after',
  'before',
  'does',
  'from',
  'have',
  'help',
  'into',
  'should',
  'that',
  'their',
  'there',
  'this',
  'what',
  'when',
  'where',
  'which',
  'with',
  'would',
]);

function tokenizeHelpQuery(query: string): string[] {
  return uniqueHelpStrings(query
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .split(/\s+/)
    .map((token) => token.trim())
    .filter((token) => token.length > 2 && !HELP_STOP_WORDS.has(token)));
}

function scoreField(value: string, tokens: string[], weight: number) {
  const haystack = value.toLowerCase();
  return tokens.reduce((score, token) => score + (haystack.includes(token) ? weight : 0), 0);
}

function getMatchedTerms(article: HelpArticle, tokens: string[]) {
  const haystack = [
    article.title,
    article.summary,
    article.body,
    article.section,
    ...article.appliesTo,
    ...article.steps,
    ...(article.tips || []),
    ...(article.relatedTabs || []),
  ].join(' ').toLowerCase();
  return tokens.filter((token) => haystack.includes(token));
}

function getArticleExcerpt(article: HelpArticle, tokens: string[]) {
  const candidates = [
    article.summary,
    article.body,
    ...article.steps,
    ...(article.tips || []),
  ];
  return candidates.find((candidate) => {
    const lower = candidate.toLowerCase();
    return tokens.some((token) => lower.includes(token));
  }) || article.summary;
}

function getHelpAnswerConfidence(matches: HelpAssistantMatch[]): HelpAssistantConfidence {
  const top = matches[0];
  if (!top || top.matchedTerms.length === 0) return 'low';
  if (top.score >= 22 || top.matchedTerms.length >= 3) return 'high';
  if (top.score >= 10 || top.matchedTerms.length >= 2) return 'medium';
  return 'low';
}

function getHelpContextLabel(activeTab: string) {
  if (!activeTab || activeTab === 'general') return 'manual';
  return 'page';
}

function uniqueHelpStrings(values: string[]) {
  return Array.from(new Set(values.filter(Boolean)));
}
